import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement[]} [lightboxSlides] - The lightbox slide elements.
 * @property {HTMLElement[]} [lightboxThumbnails] - The lightbox thumbnail elements.
 * @property {HTMLElement} [lightboxMain] - The lightbox main container.
 * @property {HTMLElement} [lightboxZoomButton] - The lightbox zoom button.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  #currentLightboxIndex = 0;

  /**
   * Selects a lightbox slide by index.
   *
   * @param {number} index - The index of the slide to show.
   */
  selectLightboxSlide(index) {
    const { lightboxSlides, lightboxThumbnails, lightboxZoomButton, lightboxMain } = this.refs;

    if (!lightboxSlides || !lightboxThumbnails) return;

    // Clamp index to valid range
    const maxIndex = lightboxSlides.length - 1;
    index = Math.max(0, Math.min(index, maxIndex));
    this.#currentLightboxIndex = index;

    // Update slides
    lightboxSlides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    // Update thumbnails
    lightboxThumbnails.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-selected', String(isActive));
    });

    // Update zoom button to open at correct index
    if (lightboxZoomButton) {
      lightboxZoomButton.setAttribute('on:click', `/zoom/${index}`);
    }

    // Update aspect ratio if in adapt mode
    const activeSlide = lightboxSlides[index];
    if (lightboxMain && activeSlide) {
      const mediaElement = /** @type {HTMLElement | null} */ (activeSlide.querySelector('.product-media'));
      if (mediaElement) {
        const ratio = mediaElement.style.getPropertyValue('--ratio');
        if (ratio) {
          lightboxMain.style.setProperty('--media-preview-ratio', ratio);
        }
      }
    }

    // Scroll active thumbnail into view
    const activeThumbnail = lightboxThumbnails[index];
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /**
   * Navigate to previous lightbox slide.
   */
  lightboxPrev() {
    const { lightboxSlides } = this.refs;
    if (!lightboxSlides) return;

    const newIndex = this.#currentLightboxIndex <= 0 ? lightboxSlides.length - 1 : this.#currentLightboxIndex - 1;
    this.selectLightboxSlide(newIndex);
  }

  /**
   * Navigate to next lightbox slide.
   */
  lightboxNext() {
    const { lightboxSlides } = this.refs;
    if (!lightboxSlides) return;

    const newIndex = this.#currentLightboxIndex >= lightboxSlides.length - 1 ? 0 : this.#currentLightboxIndex + 1;
    this.selectLightboxSlide(newIndex);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
